/**
 * i4-synthesized external AD links (animalDetails.pas / Lambplan.pas).
 * Presentation-layer only — do not overwrite SocietyAnimalField* in ETL.
 */

import { sha1Hex } from '@/utils/sha1Hex';

const LACTANET_PEDIGREE_BASE =
  'https://genetics.lactanet.ca/query/pedigree.php';

const SHEEP_GENETICS_DETAILS_BASE =
  'http://sgsearch.sheepgenetics.org.au/Search/Details.aspx?AnimalId=';

export const CJER_LACTANET_LABEL = 'Click to view at Lactanet.ca';
export const LAMBPLAN_LINK_LABEL = 'Lambplan';

/**
 * CJER Lactanet pedigree URL.
 * i4: country = copy(soc_an_field_20, 3, 3); breed fixed JE; sex + anim_ident.
 */
export function buildCjerLactanetUrl(input: {
  countryField20: string | null | undefined;
  sex: string | null | undefined;
  animalIdent: string | null | undefined;
}): string | null {
  const country = String(input.countryField20 ?? '')
    .trim()
    .slice(2, 5);
  const sex = String(input.sex ?? '').trim();
  const regnum = String(input.animalIdent ?? '').trim();
  if (!country || !sex || !regnum) return null;

  const params = new URLSearchParams({
    breed: 'JE',
    country,
    sex,
    regnum,
  });
  return `${LACTANET_PEDIGREE_BASE}?${params.toString()}`;
}

/**
 * Sheep Genetics / Lambplan details URL.
 * i4: SHA1 hex of `2:{id-without-dashes}`, first 30 chars.
 */
export function buildLambplanUrl(
  lambplanId: string | null | undefined,
): string | null {
  const id = String(lambplanId ?? '').trim();
  if (!id) return null;
  const payload = `2:${id.replace(/-/g, '')}`;
  const hash30 = sha1Hex(payload).slice(0, 30).toLowerCase();
  return `${SHEEP_GENETICS_DETAILS_BASE}${hash30}`;
}

/** Parse AD_Col token `lambplanlink(sourceField)` → inner field name. */
export function parseLambplanLinkToken(field: string): string | null {
  const match = field.trim().match(/^lambplanlink\((.+)\)$/i);
  const inner = match?.[1]?.trim();
  return inner || null;
}

/**
 * Map an AD_Col / type-18 field token to an AnimalEbv / ebvRaw key.
 * e.g. soc_an_field_7 → societyAnimalField7; AnimalIdent → animalIdent.
 */
export function adColTokenToEbvRawKey(token: string): string {
  const t = token.trim();
  const soc = t.match(/^soc_an_field_(\d+)$/i);
  if (soc) return `societyAnimalField${soc[1]}`;

  if (/^SocietyAnimalField\d+$/i.test(t)) {
    return t.charAt(0).toLowerCase() + t.slice(1);
  }

  const known: Record<string, string> = {
    anim_ident: 'animalIdent',
    animalident: 'animalIdent',
    anim_name: 'animalName',
    animalname: 'animalName',
    sex: 'sex',
    date_of_birth: 'dateOfBirth',
    anim_tattoo: 'animalTattoo',
    anim_status: 'animalStatus',
    rego_status: 'registrationStatus',
  };
  const lower = t.toLowerCase();
  if (known[lower]) return known[lower];

  return t.charAt(0).toLowerCase() + t.slice(1);
}
